import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquarePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { ReviewWithService } from '@/utils/centreStats';
import { Stars } from './Stars';
import { ReviewCard } from './ReviewCard';

export const REVIEWS_ANCHOR = 'patient-reviews';
const INLINE_REVIEWS = 5;
// Each "Show more" reveals this many.
const PAGE_SIZE = 10;
// Upper bound on review cards in the prerendered HTML; keeps a 300-review clinic light.
// Beyond it, rows mount on the client as the visitor pages through.
const DOM_REVIEWS = 30;

// Verified reviews first, then newest, then the ones with something to read.
function sortForDisplay(reviews: ReviewWithService[]): ReviewWithService[] {
  return [...reviews].sort((a, b) => {
    if (!!a.verified !== !!b.verified) return a.verified ? -1 : 1;
    if ((a.date ?? '') !== (b.date ?? '')) return (b.date ?? '').localeCompare(a.date ?? '');
    return (b.comment?.length ?? 0) - (a.comment?.length ?? 0);
  });
}

/** Reviews are written from the customer's booking history after a session. */
function RateVisitLink() {
  return (
    <Button asChild variant="ghost" size="sm" className="text-primary">
      <Link to="/history">
        <MessageSquarePlus className="size-4" /> Rate your visit
      </Link>
    </Button>
  );
}

/**
 * Inline, indexable reviews. Every card (up to DOM_REVIEWS) is in the HTML so
 * the prerender and Google see them all; only the first few are visible until
 * "Show all" expands the list in place. No modal, no nested scroll.
 */
export function PatientReviews({ reviews, rating }: { reviews: ReviewWithService[]; rating: number | null }) {
  const [visible, setVisible] = useState(INLINE_REVIEWS);
  const sorted = sortForDisplay(reviews);
  // Prerender gets DOM_REVIEWS rows; paging past that mounts the rest client-side.
  const rendered = sorted.slice(0, Math.max(DOM_REVIEWS, visible));
  const remaining = reviews.length - visible;
  const allShown = remaining <= 0;

  return (
    <section className="mt-6 rounded-2xl border bg-card p-6 shadow-sm" aria-labelledby={REVIEWS_ANCHOR}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h2 id={REVIEWS_ANCHOR} className="scroll-mt-24 text-xl font-bold tracking-tight text-foreground">
            Patient reviews
          </h2>
          {rating !== null && (
            <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Stars value={rating} className="size-3.5" />
              <span className="font-semibold text-foreground">{rating.toFixed(1)}</span>
              <span aria-hidden>·</span>
              {reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}
            </span>
          )}
        </div>
        <RateVisitLink />
      </div>

      {reviews.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          No reviews yet. Booked a session here? Rate it from your history once it is done.
        </p>
      ) : (
        <>
          <ul className="mt-3 divide-y">
            {rendered.map((r, i) => (
              <ReviewCard key={i} r={r} className={i >= visible ? 'hidden' : ''} />
            ))}
          </ul>
          {reviews.length > INLINE_REVIEWS && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setVisible(allShown ? INLINE_REVIEWS : Math.min(reviews.length, visible + PAGE_SIZE))}
                aria-controls={REVIEWS_ANCHOR}
              >
                {allShown ? 'Show fewer' : 'Show more reviews'}
              </Button>
              <span className="text-xs text-muted-foreground">
                Showing {Math.min(visible, reviews.length)} of {reviews.length}
              </span>
            </div>
          )}
        </>
      )}
    </section>
  );
}
